import { app, auth } from "@/firebaseConfig";
import { isSongLanguage, type SongLanguage } from "@/utils/songLanguage";

/** A listed song, by what the repertoire filters it on. */
export interface SongCatalogueEntry {
  id: string;
  title: string;
  titleLowercase: string;
  author: string;
  /** Null when its words tell no language; unset when not looked at yet. */
  language?: SongLanguage | null;
}

type RestValue = { stringValue?: string; nullValue?: null };
type RestRow = {
  document?: { name: string; fields?: Record<string, RestValue> };
};

/**
 * Every listed song of an organisation, by title, with only the fields its
 * filters need. Read over the Firestore REST API, as the page cursors are
 * (`pageCursors.ts`), because the web SDK can only fetch whole documents and
 * a song's chapters are most of its size. Ordering by `title_lowercase`
 * leaves out the variants, which have none.
 */
export async function fetchSongCatalogue(
  account: number,
): Promise<SongCatalogueEntry[]> {
  const idToken = await auth.currentUser?.getIdToken();
  const response = await fetch(
    `https://firestore.googleapis.com/v1/projects/${app.options.projectId}/databases/(default)/documents:runQuery`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
      },
      body: JSON.stringify({
        structuredQuery: {
          select: {
            fields: ["title", "title_lowercase", "author", "language"].map(
              (fieldPath) => ({ fieldPath }),
            ),
          },
          from: [{ collectionId: "songs" }],
          where: {
            fieldFilter: {
              field: { fieldPath: "account" },
              op: "EQUAL",
              value: { integerValue: String(account) },
            },
          },
          orderBy: [
            { field: { fieldPath: "title_lowercase" }, direction: "ASCENDING" },
            { field: { fieldPath: "__name__" }, direction: "ASCENDING" },
          ],
        },
      }),
    },
  );
  if (!response.ok) {
    throw new Error(`Listing songs failed (${response.status})`);
  }

  // An empty result is still one row, holding only the read time.
  return ((await response.json()) as RestRow[]).flatMap((row) => {
    if (!row.document) return [];
    const fields = row.document.fields ?? {};
    const entry: SongCatalogueEntry = {
      id: row.document.name.split("/").pop() ?? "",
      title: fields.title?.stringValue ?? "",
      titleLowercase: fields.title_lowercase?.stringValue ?? "",
      author: fields.author?.stringValue ?? "",
    };
    if ("language" in fields) {
      const language = fields.language.stringValue;
      entry.language = isSongLanguage(language) ? language : null;
    }
    return [entry];
  });
}
